/* ============================================================
 * Greater Glory Coffee — theme.js (Shopify build)
 * ============================================================
 * Modules (top-down):
 *   1. Section-level reveal-on-scroll
 *   2. Hero bag PNG-sequence player (uses window.GG_BAG_FRAMES)
 *   3. Nav logo morph on scroll
 *   4. Cart — Shopify Ajax API (/cart/add.js, /cart/change.js, /cart.js)
 *   5. Add-to-cart buttons (variant-id driven)
 *
 * The cart module refreshes the drawer body by fetching the URL
 * /?section_id=cart-drawer-contents which Shopify renders in
 * isolation. This pattern keeps Liquid as the source of truth for
 * markup — no client-side templating needed.
 * ============================================================ */

// Section-level reveal: when a section enters the viewport, trigger ALL
  // .reveal elements inside it at once (they still stagger via --reveal-delay).
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.querySelectorAll('.reveal').forEach(r => r.classList.add('in'));
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0, rootMargin: '0px 0px 20% 0px' });  // fire when section is still 20% below viewport — no more late/blank sections when scrolling fast
  document.querySelectorAll(
    // Homepage sections
    '.hero-frame, .shop, .bundle-bar, .mission, .donate, .footer, ' +
    // Interior page sections (Cause, Mission, Partners, Donate)
    '.page-hero, .text-block, .stats-block, .reforms-block, .cta-block, ' +
    '.compare-block, .pillars-block, .org-support-block, ' +
    '.impact-meter, .partner-cards, .nominate-block'
  ).forEach(sec => io.observe(sec));

  // SAFETY NET: force-reveal everything after 1.2s no matter what.
  // The scroll-reveal is a nice-to-have, not a requirement — if the
  // observer misfires, a section selector is missing, or a section is
  // taller than the viewport, elements must NEVER stay stuck invisible.
  // This guarantees all content is shown within 1.2s of load.
  setTimeout(() => {
    document.querySelectorAll('.reveal:not(.in)').forEach(el => el.classList.add('in'));
  }, 1200);

  // Also reveal anything still hidden the moment the page finishes loading,
  // as a second guard for slow connections.
  window.addEventListener('load', () => {
    document.querySelectorAll('.reveal:not(.in)').forEach(el => el.classList.add('in'));
  });

  /* ===== Hero bag — PNG sequence player =====
     The bag rotates through Hope → Love → Joy → Peace and back, with a
     pause at each "rest" frame where one blend faces the camera.
     The paired photo crossfades to match the current blend. */
  const TOTAL_FRAMES = 150;
  const FRAME_MS = 33;          // ~30fps during the spin
  const PAUSE_MS = 3200;        // hold at each rest frame
  const PHOTO_LEAD_MS = 600;    // start photo crossfade slightly before rest

  // Rest frames (where the bag faces forward showing a blend) and the
  // matching photo SKU to crossfade to. Discovered from the source sequence.
  const REST_FRAMES = [
    { frame: 0,   sku: 'hope'  },
    { frame: 37,  sku: 'love'  },
    { frame: 75,  sku: 'joy'   },
    { frame: 112, sku: 'peace' },
  ];

  const bagFrame = document.getElementById('bagFrame');

  // GUARD: the bag animation + its 150-frame preload only exist on the
  // homepage hero. On every other page (shop, cause, mission, etc.) there
  // is no #bagFrame element — so skip all of this entirely. Previously the
  // preload ran on ALL pages, wasting bandwidth downloading frames that
  // were never displayed. Bail early if the bag isn't on this page.
  if (bagFrame) {

  const frameUrl = i => (window.GG_BAG_FRAMES && window.GG_BAG_FRAMES[i])
    || (typeof Shopify !== 'undefined' && Shopify.theme && Shopify.theme.bagFrames && Shopify.theme.bagFrames[i])
    || '';  // Liquid pre-renders the 150 URLs into window.GG_BAG_FRAMES — see snippets/bag-frames.liquid

  // Preloading strategy — was blocking the whole homepage:
  // Old behavior fired 150 simultaneous requests (~5.4MB) at page load,
  // saturating the browser's 6-connection cap and starving product images,
  // hero photos, and CSS. New behavior:
  //   1. Preload ONLY the 4 rest frames immediately (so the SKU-photo pairing
  //      lands cleanly on first cycle).
  //   2. Defer the other 146 frames to the browser's idle callback — they
  //      trickle in after the page is interactive, without competing with
  //      critical resources.
  const REST_INDICES = [0, 37, 75, 112];
  const preloaded = [];
  const preloadFrame = (i) => {
    const img = new Image();
    img.src = frameUrl(i);
    preloaded[i] = img;
  };

  REST_INDICES.forEach(preloadFrame);

  const preloadRest = () => {
    for (let i = 0; i < TOTAL_FRAMES; i++) {
      if (!preloaded[i]) preloadFrame(i);
    }
  };
  if ('requestIdleCallback' in window) {
    requestIdleCallback(preloadRest, { timeout: 4000 });
  } else {
    setTimeout(preloadRest, 2000);
  }

  function setPhoto(sku) {
    document.querySelectorAll('.hero-img').forEach(el => {
      el.classList.toggle('is-active', el.dataset.sku === sku);
    });
  }

  // Initial state matches the bag's starting rest frame (HOPE)
  setPhoto('hope');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!reduceMotion) {
    let frame = 0;
    let timer;
    const restMap = new Map(REST_FRAMES.map(r => [r.frame, r.sku]));

    function tick() {
      frame = (frame + 1) % TOTAL_FRAMES;
      bagFrame.src = frameUrl(frame);

      // Crossfade the photo a hair before the bag actually lands on rest
      const lookahead = (frame + Math.round(PHOTO_LEAD_MS / FRAME_MS)) % TOTAL_FRAMES;
      const upcomingSku = restMap.get(lookahead);
      if (upcomingSku) setPhoto(upcomingSku);

      const isRest = restMap.has(frame);
      timer = setTimeout(tick, isRest ? PAUSE_MS : FRAME_MS);
    }

    // First pause: hold on the starting blend before kicking off the spin
    timer = setTimeout(tick, PAUSE_MS);
  }

  } // end if (bagFrame) — bag animation only runs on the homepage hero

  // ===== Donate card interactions =====
  (function donateCard() {
    const tiers = document.querySelectorAll('.donate-card .tier');
    const freqBtns = document.querySelectorAll('.donate-card .freq-btn');
    const customInput = document.querySelector('.donate-card .custom-amount input');
    const donateBtn = document.getElementById('donateBtn');
    const donateBtnLabel = donateBtn ? donateBtn.querySelector('.donate-btn-label') : null;
    if (!donateBtn || !donateBtnLabel) return;

    let amount = 50;
    let frequency = 'monthly';

    function renderBtn() {
      const suffix = frequency === 'monthly' ? '/ Month' : '';
      donateBtnLabel.textContent = `Donate $${amount} ${suffix}`.trim();
    }
    renderBtn();

    tiers.forEach(t => {
      t.addEventListener('click', () => {
        tiers.forEach(x => x.classList.remove('active'));
        t.classList.add('active');
        amount = parseInt(t.dataset.amount, 10);
        if (customInput) customInput.value = '';
        renderBtn();
      });
    });
    freqBtns.forEach(b => {
      b.addEventListener('click', () => {
        freqBtns.forEach(x => x.classList.remove('on'));
        b.classList.add('on');
        frequency = b.dataset.freq;
        renderBtn();
      });
    });
    if (customInput) {
      customInput.addEventListener('input', () => {
        const v = parseInt(customInput.value, 10);
        if (!isNaN(v) && v > 0) {
          tiers.forEach(x => x.classList.remove('active'));
          amount = v;
          renderBtn();
        }
      });
    }
    donateBtn.addEventListener('click', () => {
      const original = donateBtnLabel.textContent;
      donateBtnLabel.textContent = '✓ Thank you';
      donateBtn.disabled = true;
      setTimeout(() => {
        donateBtnLabel.textContent = original;
        donateBtn.disabled = false;
      }, 1800);
    });
  })();

  // Footer newsletter
  (function footerNewsletter() {
    const form = document.getElementById('footerNewsletter');
    if (!form) return;
    form.addEventListener('submit', e => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const btn = form.querySelector('button');
      if (!input.value || !input.value.includes('@')) {
        input.focus();
        return;
      }
      const originalText = btn.textContent;
      btn.textContent = '✓';
      btn.disabled = true;
      input.value = '';
      input.placeholder = 'Thank you — see you in your inbox.';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
        input.placeholder = 'Your email address';
      }, 2500);
    });
  })();

  // Shop — Add-to-bag confirmation
  document.querySelectorAll('.add-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const original = btn.textContent;
      btn.textContent = '✓ Added';
      btn.style.background = 'var(--gold-500)';
      btn.disabled = true;
      setTimeout(() => {
        btn.textContent = original;
        btn.style.background = '';
        btn.disabled = false;
      }, 1500);
    });
  });

  document.querySelectorAll('.btn-shop').forEach(btn => {
    btn.addEventListener('mousemove', (e) => {
      const r = btn.getBoundingClientRect();
      const x = e.clientX - (r.left + r.width / 2);
      const y = e.clientY - (r.top + r.height / 2);
      const dx = (x / r.width) * 10;
      const dy = (y / r.height) * 10;
      btn.style.transform = `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px) translateY(-2px)`;
    });
    btn.addEventListener('mouseleave', () => { btn.style.transform = ''; });
  });

  // ===== Logo morph + adaptive color
  //   - On scroll past threshold, the logo's max-width animates down to
  //     just the mark portion (the wordmark portion is clipped away by overflow)
  //   - The image src swaps between light and dark variants based on which
  //     section the logo sits over
  (function brandLogo() {
    const logo = document.getElementById('brandLogo');
    if (!logo) return;
    const collapseThreshold = 80;
    const darkSelectors = ['.hero', '.bundle-bar', '.donate', '.footer'];
    let ticking = false;
    function update() {
      const sy = window.scrollY;
      logo.classList.toggle('is-collapsed', sy > collapseThreshold);

      // Probe the page at the logo's vertical position to decide which
      // variant to show
      const probeY = 46;
      let isOnDark = false;
      darkSelectors.forEach(sel => {
        const el = document.querySelector(sel);
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.top <= probeY && r.bottom > probeY) isOnDark = true;
      });
      logo.classList.toggle('on-light', !isOnDark);
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  })();

  // ===== Cart =====
  /* ===== Cart — Shopify Ajax cart API =====
     Uses the standard endpoints:
       POST /cart/add.js         -> add item (returns line item)
       POST /cart/change.js      -> change qty or remove (qty: 0)
       GET  /cart.js             -> current cart state
       GET  /?section_id=cart-drawer-contents
                                 -> re-render the drawer contents Liquid
     All cart UI is driven from the rendered Liquid; this script only
     manages the open/close state, the badge count, and the fetches.
  */
  (function cart() {
    const drawer    = document.getElementById('cartDrawer');
    const overlay   = document.getElementById('cartOverlay');
    const closeBtn  = document.getElementById('cartCloseBtn');
    const cartBtn   = document.getElementById('cartBtn');
    const countEl   = document.getElementById('cartCount');
    const bodyEl    = document.getElementById('cartDrawerBody');
    if (!drawer || !cartBtn) return;

    function setCount(n) {
      countEl.textContent = n;
      countEl.classList.toggle('show', n > 0);
    }

    function open() {
      drawer.classList.add('is-open');
      overlay && overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      drawer.classList.remove('is-open');
      overlay && overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    async function refreshDrawer() {
      const r = await fetch('/?section_id=cart-drawer-contents', { credentials: 'same-origin' });
      const html = await r.text();
      // Shopify wraps section content in a <div id="shopify-section-cart-drawer-contents">.
      // We extract its inner HTML and drop it into the drawer body.
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const section = tmp.querySelector('[id^="shopify-section-"]') || tmp;
      bodyEl.innerHTML = section.innerHTML;
    }

    async function refreshAll() {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      setCount(cart.item_count);
      await refreshDrawer();
    }

    async function addVariant(variantId, quantity) {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        console.error('Cart add failed', err);
        return null;
      }
      return r.json();
    }

    async function changeLine(line, quantity) {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      });
      return r.json();
    }

    cartBtn.addEventListener('click', () => { refreshDrawer(); open(); });
    closeBtn && closeBtn.addEventListener('click', close);
    overlay  && overlay.addEventListener('click', close);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Delegated handlers for qty buttons + remove inside the drawer body.
    // (Re-applies after refreshDrawer since markup is replaced.)
    bodyEl && bodyEl.addEventListener('click', async (e) => {
      const qtyBtn = e.target.closest('.qty-btn');
      if (qtyBtn) {
        await changeLine(parseInt(qtyBtn.dataset.line, 10), parseInt(qtyBtn.dataset.qty, 10));
        await refreshAll();
        return;
      }
      const removeBtn = e.target.closest('.cart-line-remove');
      if (removeBtn) {
        await changeLine(parseInt(removeBtn.dataset.line, 10), 0);
        await refreshAll();
        return;
      }
    });

    // Wire Add-to-bag buttons across the page (variant-id driven).
    // data-quantity is optional and read fresh at click time so a PDP
    // quantity stepper can update it without re-binding this handler.
    document.querySelectorAll('.add-btn[data-variant-id], .shop-cta-btn[data-variant-id]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        if (btn.tagName === 'A') e.preventDefault();
        const vid = btn.dataset.variantId;
        if (!vid) return;
        const qty = parseInt(btn.dataset.quantity, 10) || 1;
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        const result = await addVariant(vid, qty);
        if (result) {
          btn.textContent = '✓ Added';
          await refreshAll();
          setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1200);
          // Optional: auto-open drawer on add. Uncomment if desired.
          // open();
        } else {
          btn.textContent = 'Try again';
          setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1500);
        }
      });
    });

    // Donate buttons — also Shopify variant-id, but with custom-amount support
    document.querySelectorAll('.donate-tier[data-variant-id]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const vid = btn.dataset.variantId;
        const isCustom = btn.dataset.isCustom === 'true';
        let qty = 1;
        if (isCustom) {
          const amount = prompt('Enter donation amount (USD):', '50');
          if (!amount || isNaN(amount)) return;
          // Custom variant is $1 unit; qty = dollar amount
          qty = Math.max(1, Math.floor(Number(amount)));
        }
        const original = btn.querySelector('.amount').textContent;
        btn.disabled = true;
        const result = await addVariant(vid, qty);
        if (result) {
          await refreshAll();
          open();
          setTimeout(() => { btn.disabled = false; }, 600);
        } else {
          btn.disabled = false;
          alert('Sorry — could not add donation. Please try again.');
        }
      });
    });

    // Set initial badge count from Shopify-rendered cart
    if (window.Shopify && Shopify.cart) {
      setCount(Shopify.cart.item_count || 0);
    } else if (countEl) {
      // Liquid pre-renders cart.item_count into the badge; trust it.
      setCount(parseInt(countEl.textContent, 10) || 0);
    }
  })();

/* ===== Roast filter (Shop page) ===== */
(function roastFilter() {
  const buttons = document.querySelectorAll('.roast-btn');
  if (!buttons.length) return;
  const products = document.querySelectorAll('.product[data-roast]');
  buttons.forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      buttons.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      const roast = btn.dataset.roast;
      products.forEach(p => {
        const matches = roast === 'all' || p.dataset.roast === roast;
        p.style.display = matches ? '' : 'none';
      });
    });
  });
})();

/* ===== Impact meter count-up (Partners page) ===== */
(function impactMeter() {
  const numEl = document.querySelector('.impact-num');
  if (!numEl) return;
  const target = parseInt(numEl.dataset.target || '0', 10);
  const goal = 100000;
  const progressFill = document.querySelector('.impact-progress-fill');
  let played = false;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (!e.isIntersecting || played) return;
      played = true;
      const start = performance.now();
      const duration = 1800;
      function tick(now) {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        numEl.textContent = Math.floor(target * eased).toLocaleString('en-US');
        if (t < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
      if (progressFill) progressFill.style.width = Math.min(100, (target / goal) * 100) + '%';
    });
  }, { threshold: 0.3 });
  observer.observe(numEl);
})();

/* ===== Feed filter (Feed page — hidden for launch) ===== */
(function feedFilter() {
  const pills = document.querySelectorAll('.feed-pill');
  if (!pills.length) return;
  const cards = document.querySelectorAll('[data-feed-type]');
  pills.forEach(pill => {
    pill.addEventListener('click', () => {
      pills.forEach(p => p.classList.remove('is-active'));
      pill.classList.add('is-active');
      const f = pill.dataset.filter;
      cards.forEach(c => {
        c.style.display = (f === 'all' || c.dataset.feedType === f) ? '' : 'none';
      });
    });
  });
})();

/* ===== Product detail page (gallery, variant picker, quantity) ===== */
(function pdp() {
  const page = document.querySelector('.pdp-buy');
  if (!page) return;

  const addBtn = page.querySelector('.pdp-add[data-variant-id]');
  let qty = 1;

  // ---- Gallery ----
  page.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-pdp-thumb]');
    if (thumb) {
      const shots = page.querySelectorAll('.pdp-shot');
      const i = parseInt(thumb.dataset.pdpThumb, 10);
      page.querySelectorAll('[data-pdp-thumb]').forEach((b) => b.classList.toggle('is-active', b === thumb));
      shots.forEach((shot, si) => shot.classList.toggle('is-active', si === i));
    }
  });

  // ---- Variant picker ----
  const variantsEl = page.querySelector('[data-pdp-variants]');
  const variants = variantsEl ? JSON.parse(variantsEl.textContent) : null;
  const variantInput = page.querySelector('[data-pdp-variant-input]');
  const priceEl = page.querySelector('[data-pdp-add-price]');

  function moneyFromCents(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function currentSelection() {
    const selection = [];
    page.querySelectorAll('[data-pdp-option-index]').forEach((field) => {
      const active = field.querySelector('.pdp-size.is-active');
      selection[parseInt(field.dataset.pdpOptionIndex, 10)] = active ? active.dataset.optionValue : null;
    });
    return selection;
  }

  function syncVariant() {
    if (!variants) return;
    const selection = currentSelection();
    const match = variants.find((v) => v.options.every((opt, i) => opt === selection[i]));
    if (!match) return;
    if (variantInput) variantInput.value = match.id;
    if (addBtn) {
      addBtn.dataset.variantId = match.id;
      addBtn.disabled = !match.available;
      addBtn.querySelector('.pdp-add-label').textContent = match.available ? 'Add to bag' : 'Sold out';
    }
    updateAddPrice(match.price);
  }

  function updateAddPrice(unitPriceCents) {
    if (!priceEl) return;
    priceEl.textContent = '— ' + moneyFromCents(unitPriceCents * qty);
  }

  page.addEventListener('click', (e) => {
    const sizeBtn = e.target.closest('.pdp-size');
    if (sizeBtn) {
      const field = sizeBtn.closest('[data-pdp-option-index]');
      field.querySelectorAll('.pdp-size').forEach((b) => {
        const on = b === sizeBtn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      syncVariant();
    }
  });

  // ---- Quantity ----
  const qtyValueEl = page.querySelector('[data-pdp-qty-value]');

  function currentUnitPriceCents() {
    if (!variants) return null;
    const match = variants.find((v) => String(v.id) === String(addBtn.dataset.variantId));
    return match ? match.price : null;
  }

  page.addEventListener('click', (e) => {
    const qtyBtn = e.target.closest('[data-pdp-qty]');
    if (qtyBtn) {
      qty = Math.max(1, qty + (qtyBtn.dataset.pdpQty === 'inc' ? 1 : -1));
      if (qtyValueEl) qtyValueEl.textContent = qty;
      if (addBtn) addBtn.dataset.quantity = qty;
      const unitPrice = currentUnitPriceCents();
      if (unitPrice != null) updateAddPrice(unitPrice);
    }
  });

  if (addBtn) addBtn.dataset.quantity = qty;
})();
